import withKycRequired from "@rentiohq/shared-frontend/dist/hoc/withKycRequired";
import * as requirementsSelectors from "@rentiohq/shared-frontend/dist/redux/requirements/requirements.selectors";
import { IPartialRootState } from "@rentiohq/shared-frontend/dist/redux/types";
import * as paymentRequestHooks from "@rentiohq/shared-frontend/dist/reduxV2/paymentRequest/paymentRequest.hooks";
import { EPaymentMethod } from "@rentiohq/shared-frontend/dist/types/payment.types";
import { EPaymentRequestStatus } from "@rentiohq/shared-frontend/dist/types/paymentRequest.types";
import { includesAny } from "@rentiohq/shared-frontend/dist/utils/array.utils";
import { max } from "@rentiohq/shared-frontend/dist/utils/date-fns.utils";
import { formatDate } from "@rentiohq/shared-frontend/dist/utils/date.utils";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { formatCurrency } from "@rentiohq/shared-frontend/dist/utils/number.utils";
import {
  getPeriodForPaymentRequestItem,
  getSepaDueDate,
  getSepaLatestApprovalDate,
} from "@rentiohq/shared-frontend/dist/utils/payment.utils";
import * as paymentOrderUtils from "@rentiohq/shared-frontend/dist/utils/paymentOrder.utils";
import { join } from "@rentiohq/shared-frontend/dist/utils/string.utils";
import { ASSETS } from "@rentiohq/web-shared-next/dist/assets";
import {
  Box,
  Button,
  MainCard,
  Typography,
  Divider,
  DataList,
  Stack,
  SpinningLoader,
} from "@rentiohq/web-shared-next/dist/ui-components";
import { enqueueSnackbar } from "@rentiohq/web-shared-next/dist/utils/snackbar";
import { EButtonVariants } from "@rentiohq/web-shared-next/dist/utils/types/button";
import { uniq } from "lodash";
import React from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { PaymentDetailStyles } from "./PaymentDetail.styles";

const NOW = new Date();

const PaymentDetailBase = () => {
  const sx = PaymentDetailStyles();
  const params = useParams();
  const navigate = useNavigate();

  const requirements = useSelector((state: IPartialRootState) =>
    requirementsSelectors.getRequirements(state),
  );

  const paymentRequestId = params.paymentRequestId
    ? parseInt(params.paymentRequestId, 0)
    : undefined;

  const {
    detail: paymentRequest,
    fetchError,
    isFetching,
  } = paymentRequestHooks.useDetail({
    shouldRefetch: true,
    id: paymentRequestId,
  });

  React.useEffect(() => {
    if (fetchError?.message) {
      enqueueSnackbar({
        message: fetchError.message,
        variant: "error",
      });
    }
  }, [fetchError]);

  React.useEffect(() => {
    if (
      paymentRequest &&
      paymentRequest.status === EPaymentRequestStatus.Paid
    ) {
      enqueueSnackbar({
        message: getLocalizedText("payment.error.already_paid.title"),
        variant: "error",
      });
      navigate("/payments");
    }
  }, [paymentRequest]);

  if (!paymentRequest) {
    return null;
  }

  const {
    allowedPaymentMethods = [],
    paymentRequestItems,
    dueDateAt,
    originalAmount,
  } = paymentRequest || {};

  const sepaApprovalDate = formatDate(
    getSepaLatestApprovalDate(paymentRequest, requirements?.sepaBankDaysOffset),
  );

  const sepaDueDate = formatDate(
    getSepaDueDate(paymentRequest, requirements?.sepaBankDaysOffset),
  );

  const dueDate = formatDate(max([NOW, dueDateAt]));

  const paymentMethods = join(
    allowedPaymentMethods
      ?.map(x => getLocalizedText(`payment_request.method.${x}`))
      .sort(),
    ", ",
    ` ${getLocalizedText("system.or").toLowerCase()} `,
  );

  const renderPaymentMethodIcons = () => {
    const paymentMethodsMapped = uniq(
      allowedPaymentMethods.map(paymentMethod => {
        if (
          paymentMethod === EPaymentMethod.SepaOnce ||
          paymentMethod === EPaymentMethod.SepaRecurring
        ) {
          return EPaymentMethod.Sepa;
        }
        return paymentMethod;
      }),
    );

    return paymentMethodsMapped.map(paymentMethod =>
      renderPaymentMethodIcon(paymentMethod),
    );
  };

  const renderPaymentMethodIcon = (paymentMethod: EPaymentMethod) => {
    switch (paymentMethod) {
      case EPaymentMethod.Sepa:
      case EPaymentMethod.SepaOnce:
      case EPaymentMethod.SepaRecurring:
        return <img src={ASSETS.sepa} alt="SEPA" height="12px" />;

      // case EPaymentMethod.Ibanisation:
      //   return (
      //     <img src={ASSETS.bankTransfer} alt="bank transfer" height="14px" />
      //   );

      case EPaymentMethod.Payconiq:
        return <img src={ASSETS.payconiq} alt="Payconiq" height="20px" />;

      case EPaymentMethod.Ideal:
        return <img src={ASSETS.idealWero} alt="iDEAL-Wero" height="20px" />;

      case EPaymentMethod.Bancontact:
        return <img src={ASSETS.bancontact} alt="Bancontact" height="20px" />;

      default:
        break;
    }
  };

  return (
    <MainCard content={false}>
      <Typography variant="h4" sx={sx.content.heading}>
        {getLocalizedText("tenant.payment_detail.title")}
      </Typography>
      <Divider />
      <Box p={3}>
        {isFetching ? (
          <SpinningLoader />
        ) : (
          <>
            <DataList showDivider={true} gap={2}>
              {paymentRequestItems.map(paymentRequestItem => {
                const { id, paymentOrder, amount } = paymentRequestItem;
                const period =
                  getPeriodForPaymentRequestItem(paymentRequestItem);

                return (
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    key={id}
                  >
                    <Box>
                      <Typography variant="h6" sx={sx.content.label}>
                        {paymentOrderUtils.getTitle(paymentOrder)}
                      </Typography>
                      {period && (
                        <Typography variant="subtitle1" sx={sx.content.date}>
                          {period}
                        </Typography>
                      )}
                    </Box>
                    <Box>
                      <Typography variant="subtitle2">
                        {formatCurrency(amount, true)}
                      </Typography>
                    </Box>
                  </Stack>
                );
              })}
            </DataList>
            <Divider sx={sx.content.divider} />
            <Stack direction="row" justifyContent="space-between" mt={2}>
              <Typography variant="h5" sx={sx.content.label}>
                {getLocalizedText("payment.receipt.total")}
              </Typography>
              <Typography variant="body1">
                {formatCurrency(originalAmount, true)}
              </Typography>
            </Stack>
            <Stack
              direction="row"
              gap={1.5}
              mt={3}
              flexWrap="wrap"
              alignItems="center"
            >
              <Button
                variant={EButtonVariants.Contained}
                sx={sx.content.btn}
                onClick={() =>
                  navigate(`/payment/${params.paymentRequestId}/payment-method`)
                }
              >
                {getLocalizedText("tenant.start_payment.btn.label")}
              </Button>
              <span
                onClick={() =>
                  navigate(`/payment/${params.paymentRequestId}/payment-method`)
                }
              >
                <Stack direction="row" alignItems="center" gap={2}>
                  {renderPaymentMethodIcons()}
                </Stack>
              </span>
            </Stack>
            {paymentRequestItems.length > 0 && (
              <Box sx={sx.content.bannerWrapper}>
                <Typography variant="h6" sx={sx.content.bannerTitle}>
                  {getLocalizedText("payment.receipt.pay_before.unified", {
                    sepaApprovalDate,
                    sepaDueDate,
                    dueDate,
                    paymentMethods,
                  })}
                </Typography>
                {includesAny(paymentRequest.allowedPaymentMethods, [
                  EPaymentMethod.Sepa,
                  EPaymentMethod.SepaOnce,
                  EPaymentMethod.SepaRecurring,
                ]) && (
                  <Typography variant="body2" sx={sx.content.bannerDetail}>
                    {getLocalizedText("payment.receipt.pay_completed.unified", {
                      sepaApprovalDate,
                      sepaDueDate,
                      dueDate,
                      paymentMethods,
                    })}
                  </Typography>
                )}
              </Box>
            )}
          </>
        )}
      </Box>
    </MainCard>
  );
};

export const PaymentDetail = withKycRequired(PaymentDetailBase);
